import {
  Box,
  IconButton,
  Dialog,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useRef, useState } from "react";
import UserTitle from "./UserTitle";
import { Close } from "@mui/icons-material";
import CreateComment from "./CreateComment";
import ImagePostContent from "./ImagePostContent";
import { usePostComments } from "../api/getPostComments";
import { ParentComment } from "./Comment";
import LikePost from "./LikePost";
import VideoPostContent from "./PostContent/VideoPostContent";
import TextPostContent from "./PostContent/TextPostContent";
import PostActions from "./PostActions";
import { makeStyles } from "@mui/styles";
import { cn } from "../../../shared/utils/cn";

const useStyles = makeStyles((theme) => ({
  "ExpandedPost-PostContent": (props) => ({
    width: "100dvw",
    height: "max-content",
    [theme.breakpoints.up("md")]: {
      height: "100% !important",
      width: "max-content",
    },

    aspectRatio: props.aspectRatio,

    [theme.breakpoints.between("md", "lg")]: {
      aspectRatio: "1 / 1",
    },
  }),

  "ExpandedPost-Paper-Root": {
    [theme.breakpoints.up("md")]: {
      "& > .post_content": {
        flexGrow: 1,
      },
      flexDirection: "row !important",
    },
  },
  "ExpandedPost-Paper-16/9": {
    [theme.breakpoints.between("md", "lg")]: {
      height: "45dvw !important",
    },
    [theme.breakpoints.between("lg", "xl")]: {
      height: "35dvw !important",
    },
    [theme.breakpoints.up("xl")]: {
      height: "30dvw !important",
    },
  },
  "ExpandedPost-Paper-4/5": {
    [theme.breakpoints.between("md", "lg")]: {
      height: "45dvw !important",
    },
    [theme.breakpoints.between("lg", "xl")]: {
      height: "50dvw !important",
    },
    [theme.breakpoints.up("xl")]: {
      height: "50dvw !important",
    },
  },
  "ExpandedPost-Paper-1/1": {
    [theme.breakpoints.between("md", "lg")]: {
      height: "45dvw !important",
    },
    [theme.breakpoints.between("lg", "xl")]: {
      height: "40dvw !important",
    },
    [theme.breakpoints.up("xl")]: {
      height: "40dvw !important",
    },
  },
}));

const CommentSectionWrapper = styled("div")(({ theme }) => ({
  width: "min(100vw, 405px)",
  maxWidth: "500px",
  display: "flex",
  flexDirection: "column",
  position: "relative",

  paddingBottom: "175px",
  height: "100%",
  [theme.breakpoints.up("md")]: {
    minWidth: "405px",
  },
}));

const CommentSectionHeader = styled("div")(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  borderBottom: `1px solid ${theme.palette.grey["200"]}`,
  display: "flex",
  justifyContent: "space-between",
}));

const CommentSection = styled("div")(({ theme }) => ({
  flexGrow: "1",

  height: "max-content",
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
}));

const CommentSectionFooter = styled("div")(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.grey["200"]}`,
  position: "absolute",
  bottom: "0",
  height: "175px",
  width: "100%",
}));

const RenderContent = ({ postData }) => {
  const aspectRatio = postData.proporcaoImagem;
  const classes = useStyles({ aspectRatio });

  switch (postData.TipoPostagem) {
    case "IMAGEM":
      return (
        <ImagePostContent
          postData={postData}
          className={cn(classes["ExpandedPost-PostContent"])}
        />
      );

    case "VIDEO":
      return (
        <VideoPostContent
          postData={postData}
          className={cn(classes["ExpandedPost-PostContent"])}
        />
      );

    case "TEXTO":
      return (
        <TextPostContent
          postData={postData}
          className={cn(
            classes["ExpandedPost-PostContent"],
            classes["ExpandedPost-TextPost"]
          )}
        />
      );
    default:
      return <></>;
  }
};

export default function SocialMediaPostExpanded({ open, postData, onClose }) {
  const postId = postData?.postagemid;
  const classes = useStyles({ aspectRatio: "1 / 1" });
  const aspectRatio = postData.proporcaoImagem;
  const parsedAspectRatio = aspectRatio.replaceAll(" ", "");
  const commentsQuery = usePostComments({
    postId,
    config: {
      enabled: open,
    },
  });

  const [replyingTo, setReplyingTo] = useState(0);
  const commentInputRef = useRef();

  const handleClose = () => {
    onClose();
  };

  const handleClickReplyComment = (commentId) => {
    setReplyingTo(commentId);
    commentInputRef.current.focus();
  };
  const theme = useTheme();

  const isDisplaySmall = useMediaQuery(theme.breakpoints.down("md"));

  const resetReplyingTo = () => {
    setReplyingTo();
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      contentAspectRatio={postData.proporcaoImagem}
      maxWidth={false}
      fullScreen={isDisplaySmall}
      PaperProps={{
        className: cn(
          classes["ExpandedPost-Paper-Root"],
          classes[`ExpandedPost-Paper-${parsedAspectRatio}`]
        ),
      }}
    >
      <RenderContent postData={postData} />

      <CommentSectionWrapper>
        <CommentSectionHeader>
          <UserTitle
            userData={{
              studentId: postData.alunoid,
              name: postData.nomeAluno,
              pictureURL: postData.fotoAluno,
            }}
          />
          <span>
            <PostActions postData={postData} />
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </span>
        </CommentSectionHeader>
        <CommentSection>
          {commentsQuery.data?.map((comment) => {
            return (
              <ParentComment
                comment={comment}
                onClickReplyComment={handleClickReplyComment}
                resetReplyingTo={resetReplyingTo}
              />
            );
          })}
        </CommentSection>
        <CommentSectionFooter>
          <Box
            component="section"
            sx={{ borderBottom: `1px solid ${theme.palette.grey["200"]}` }}
          >
            <Box sx={{ p: 2 }}>
              <Box sx={{ marginBottom: 2 }} component={"section"}>
                <LikePost
                  postId={postData?.postagemid}
                  themeId={postData?.temaid}
                  liked={postData?.liked_from_me}
                ></LikePost>
              </Box>

              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "600", marginBottom: "4px" }}
              >
                {postData?.qtd_likes
                  ? `${postData?.qtd_likes} curtida(s)`
                  : `Seja o primeiro a curtir`}
              </Typography>
              <Typography variant="subtitle2">
                {postData?.data_ultima_postagem}
              </Typography>
            </Box>
          </Box>
          <CreateComment
            postId={postId}
            replyingTo={replyingTo}
            inputRef={commentInputRef}
            resetReplyingTo={resetReplyingTo}
            onSuccess={resetReplyingTo}
          />
        </CommentSectionFooter>
      </CommentSectionWrapper>
    </Dialog>
  );
}
